import { captureException } from '@sentry/react'
import { BaseEdge, EdgeLabelRenderer, getBezierPath, useReactFlow } from 'reactflow'

import type { ReactElement } from 'react'
import type { EdgeProps } from 'reactflow'
import type { ITask } from '~/types/app-setup'

import { AUTHORIZATIONS } from '~/const'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useUpdateTaskMutation } from '~/queries/application-setup'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const AppSetupButtonEdge = ({
  id,
  markerEnd,
  source,
  sourcePosition,
  sourceX,
  sourceY,
  style = {},
  target,
  targetPosition,
  targetX,
  targetY,
}: EdgeProps): ReactElement => {
  /** Local state */

  const isActionAuthorized = useIsUserAuthorized([AUTHORIZATIONS.APP_SETUP.TASK.WRITE])

  const { getNodes, setEdges } = useReactFlow<ITask>()

  const [edgePath, labelX, labelY] = getBezierPath({
    sourcePosition,
    sourceX,
    sourceY,
    targetPosition,
    targetX,
    targetY,
  })

  /** Queries & Mutations */

  const { mutate: updateTaskMutation } = useUpdateTaskMutation()

  /** Handlers */

  const handleEdgeClick = () => {
    const nodes = getNodes()
    const targetNode = nodes.find(({ id }) => id === target)

    if (targetNode) {
      try {
        updateTaskMutation({
          payload: {
            ...targetNode.data,
            previous_task_ids: targetNode.data.previous_task_ids?.filter((id) => source !== id) || [],
          },
        })
        setEdges((edges) => edges.filter((edge) => edge.id !== id))
      } catch (e) {
        displayToast('Tasks cannot be unlinked!', 'error')
        captureException(e)
      }
    }
  }

  return (
    <>
      <BaseEdge markerEnd={markerEnd} path={edgePath} style={style} />
      {isActionAuthorized ? (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            style={{
              fontSize: 12,
              // everything inside EdgeLabelRenderer has no pointer events by default
              // if you have an interactive element, set pointer-events: all
              pointerEvents: 'all',
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            }}
          >
            <button className="app-setup-button-edge-component" onClick={handleEdgeClick}>
              ×
            </button>
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  )
}

export default AppSetupButtonEdge
